// =====================================================================
// Faz Z (2026-05-06) — Smart Recommendations
// =====================================================================
// Owner için "ne yapmalıyım" cevabı veren proaktif öneri sayfası.
//
// Action Center "şu an acil" gösterir; bu sayfa "fırsat" gösterir —
// beklemeden harekete geçilmesi gereken öneriler.
//
// 7 kategori öneri (backend Faz Z endpoint):
//   💰 price_promo           Düşük conversion ürün → indirim önerisi
//   📦 stock_reorder         Bitecek + iyi satan ürün → tedarikçi sipariş
//   ⚠️ reactivate_at_risk    Risk altında müşteri → reaktivasyon
//   🎟️ promote_unused_coupon Kullanılmamış aktif kupon → duyur
//   🎁 bundle_opportunity    Cross-sell pair → set sat
//   ⚠️ content_improve       NPS detractor yorum → kişisel kontak
//   😴 wakeup_hibernating    Kayıp müşteri kümesi → toplu mail
//
// Her öneri kart formatında: ikon + başlık + problem + action + buton.
//
// Endpoint: GET /api/admin/insights/recommendations
// =====================================================================
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Card, Typography, Grid, Chip, CircularProgress, Alert, IconButton,
  Tooltip, Button, Avatar
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import API from "../services/api";

// Priority → renk + etiket — kart sol kenarındaki şerit
const PRIORITY_META = {
  high:   { color: "#ef4444", bg: "#fef2f2", label: "Yüksek" },
  medium: { color: "#f59e0b", bg: "#fffbeb", label: "Orta"   },
  low:    { color: "#3b82f6", bg: "#eff6ff", label: "Düşük"  }
};

// Type → kategori başlığı (gruplama için)
const TYPE_LABELS = {
  price_promo:           "💰 Fiyat / Promosyon Fırsatı",
  stock_reorder:         "📦 Stok Yenileme",
  reactivate_at_risk:    "⚠️ Risk Altında Müşteri",
  promote_unused_coupon: "🎟️ Kullanılmamış Kupon",
  bundle_opportunity:    "🎁 Bundle Fırsatı",
  content_improve:       "⚠️ NPS Detractor Yorum",
  wakeup_hibernating:    "😴 Kayıp Müşteri Kümesi"
};

export default function SmartRecommendations() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");  // all | high | medium | low

  const load = () => {
    setLoading(true);
    API.get("/api/admin/insights/recommendations")
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Filter + grouping (kategori bazlı)
  const grouped = useMemo(() => {
    if (!data?.recommendations) return {};
    const recs = filter === "all"
      ? data.recommendations
      : data.recommendations.filter(r => r.priority === filter);
    // Type'a göre gruplaaa
    const g = {};
    for (const r of recs) {
      if (!g[r.type]) g[r.type] = [];
      g[r.type].push(r);
    }
    return g;
  }, [data, filter]);

  if (loading && !data) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (err) return <Alert severity="error" sx={{ m: 2 }}>{err}</Alert>;
  if (!data) return null;

  const counts = data.by_priority || { high: 0, medium: 0, low: 0 };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Smart Recommendations</Typography>
          <Typography variant="body2" color="text.secondary">
            Bugün ne yapmalıyım — proaktif öneri merkezi
          </Typography>
        </Box>
        <Tooltip title="Yenile">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {/* Priority KPI'ları — tıklayınca filter değişir */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={3}>
          <Card
            onClick={() => setFilter("all")}
            sx={{
              p: 2, borderRadius: 3, cursor: "pointer",
              border: filter === "all" ? "2px solid #1565C0" : "2px solid transparent",
              bgcolor: filter === "all" ? "#e3f2fd" : "white"
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={700}>TÜM ÖNERİLER</Typography>
            <Typography variant="h3" fontWeight={800} color="#1565C0">{data.total}</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card
            onClick={() => setFilter("high")}
            sx={{
              p: 2, borderRadius: 3, cursor: "pointer",
              border: filter === "high" ? "2px solid #ef4444" : "2px solid transparent",
              bgcolor: filter === "high" ? "#fef2f2" : "white"
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={700}>YÜKSEK</Typography>
            <Typography variant="h3" fontWeight={800} color="#ef4444">{counts.high}</Typography>
            <Typography variant="caption" color="text.disabled">acil aksiyon</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card
            onClick={() => setFilter("medium")}
            sx={{
              p: 2, borderRadius: 3, cursor: "pointer",
              border: filter === "medium" ? "2px solid #f59e0b" : "2px solid transparent",
              bgcolor: filter === "medium" ? "#fffbeb" : "white"
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={700}>ORTA</Typography>
            <Typography variant="h3" fontWeight={800} color="#f59e0b">{counts.medium}</Typography>
            <Typography variant="caption" color="text.disabled">bu hafta</Typography>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card
            onClick={() => setFilter("low")}
            sx={{
              p: 2, borderRadius: 3, cursor: "pointer",
              border: filter === "low" ? "2px solid #3b82f6" : "2px solid transparent",
              bgcolor: filter === "low" ? "#eff6ff" : "white"
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={700}>DÜŞÜK</Typography>
            <Typography variant="h3" fontWeight={800} color="#3b82f6">{counts.low}</Typography>
            <Typography variant="caption" color="text.disabled">fırsat olunca</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Boş durum */}
      {Object.keys(grouped).length === 0 && (
        <Alert severity="success" sx={{ borderRadius: 2 }} icon={<LightbulbIcon />}>
          {filter === "all"
            ? "Şu an aktif öneri yok — sistem temiz görünüyor 🎉 (saatte 1 yeniden taranır)"
            : `${PRIORITY_META[filter]?.label || filter} önceliklı öneri yok`}
        </Alert>
      )}

      {/* Kategori grup kartları */}
      {Object.entries(grouped).map(([type, items]) => (
        <Card key={type} sx={{ borderRadius: 3, mb: 3, overflow: "hidden" }}>
          <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" fontWeight={700}>{TYPE_LABELS[type] || type}</Typography>
            <Box sx={{ flex: 1 }} />
            <Chip label={`${items.length} öneri`} size="small" />
          </Box>

          {/* Öneri kartları — her biri ayrı satır */}
          <Box sx={{ p: 1.5 }}>
            {items.map((rec, idx) => {
              const pm = PRIORITY_META[rec.priority] || PRIORITY_META.medium;
              return (
                <Card
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 2, mb: 1.5,
                    borderLeft: `4px solid ${pm.color}`,
                    bgcolor: pm.bg,
                    "&:last-of-type": { mb: 0 }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    {/* Sol: ikon */}
                    <Avatar sx={{
                      bgcolor: pm.color, color: "white",
                      width: 44, height: 44, fontSize: 22
                    }}>
                      {rec.icon}
                    </Avatar>

                    {/* Orta: başlık + problem + action */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.3 }}>
                        <Typography fontWeight={700} fontSize={14}>{rec.title}</Typography>
                        <Chip
                          label={pm.label}
                          size="small"
                          sx={{
                            bgcolor: pm.color, color: "white",
                            fontWeight: 700, fontSize: 10, height: 18
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: 13 }}>
                        <strong>Sorun:</strong> {rec.problem}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600 }}>
                        <span style={{ color: pm.color }}>→ Aksiyon:</span> {rec.action}
                      </Typography>
                    </Box>

                    {/* Sağ: aksiyon butonu */}
                    {rec.link && (
                      <Button
                        variant="contained"
                        size="small"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate(rec.link)}
                        sx={{
                          bgcolor: pm.color,
                          "&:hover": { bgcolor: pm.color, opacity: 0.9 },
                          minWidth: 120, alignSelf: "center", flexShrink: 0
                        }}
                      >
                        Yap
                      </Button>
                    )}
                  </Box>
                </Card>
              );
            })}
          </Box>
        </Card>
      ))}

      {/* Footer ipucu */}
      <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#f8fafc", mt: 3 }}>
        <Typography variant="caption" color="text.secondary">
          💡 <strong>Smart Recommendations</strong>: 7 farklı veri kaynağını birleştirerek (sepete eklenen ürün konversiyon,
          stok velocity, RFM segment, kupon kullanım, cross-sell pair, NPS feedback, lifecycle segment) somut aksiyon önerileri üretir.
          Action Center "şu an acil" gösterir; bu sayfa "fırsat" gösterir.
          Sayfa yenilendiğinde 7 endpoint paralel sorgulanır (~1-2sn).
        </Typography>
      </Card>
    </Box>
  );
}
